import React, { useId } from "react";
import { shallowEqual } from "react-redux";
import { useUscDispatch, useUscSelector } from "../../../../../redux/store";
import type { FinanceOptionType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { useUscLabel } from "../../../../../utils/labels";
import { getSimplifiedTotalPriceSelector } from "../../../../../utils/priceUtils";
import UscCashPrice from "../../price/UscCashPrice";
import { setFinanceOption } from "../../../../../redux/actions/UscBasketActions";
import { enableUscMonthlyRate } from "../../../../../utils/settings";
import { type UscTrackingLocation, trackFinanceToggle } from "../../../../../utils/tracking";
import Text from "../../../../../../shared-components/toyota/styles/atoms/Text";
import Preloader from "../../../../../../shared-components/toyota/styles/molecules/Preloader";
import { SrOnly } from "../../../../../../shared-components/toyota/styles/utils/Accessibility.styles";
import * as Styles from "./UscFinanceToggle.styles";
import { showUscMonthlyDisclaimersSelector } from "../../../../../utils/disclaimerUtils";
import UscMonthlyPriceDisclaimer from "../../price/UscMonthlyPriceDisclaimer";
import InlineDisclaimer from "../../InlineDisclaimer";
import { UscDisclaimerType } from "../../../../../../shared-logic/types/UscCommonTypes";
import UscInlinePrices from "../../price/UscInlinePrices";
import useUscFinanceCalculatorButton from "../../../../../hooks/useUscFinanceCalculatorButton";
import ModalUscPersonaliseFinanceButtonWrapper from "./ModalUscPersonaliseFinanceButtonWrapper";
import PdpUscPersonaliseFinanceButton from "./PdpUscPersonaliseFinanceButton";
import { hasDualCurrency } from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";

type UscFinanceToggleType = {
    trackingLocation: UscTrackingLocation;
    inBasket?: boolean;
    inModal?: boolean;
};

const UscFinanceToggle = (props: UscFinanceToggleType): JSX.Element => {
    const { trackingLocation, inBasket, inModal } = props;
    const dispatch = useUscDispatch();
    const enableMonthly = useUscSelector(({ commonSettings, uscSettings, vehicle }) =>
        enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const showFinancePrices = useUscSelector((state) => !state.uscSettings.hideFinanceTogglePrices);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const { monthly, cashLoading, monthlyLoading } = useUscSelector(
        getSimplifiedTotalPriceSelector(true),
        shallowEqual,
    );

    const id = useId();

    const showMonthlyDisclaimers = useUscSelector(showUscMonthlyDisclaimersSelector, shallowEqual);

    const { showCalculator } = useUscFinanceCalculatorButton(trackingLocation);

    const [cashOnlyLabel, selectedCashLabel, selectedMonthlyLabel, switchToCashLabel, switchToMonthlyLabel] =
        useUscLabel([
            "uscFinanceOptionCashOnly",
            "uscFinanceOptionSelectedCash",
            "uscFinanceOptionSelectedMonthly",
            "uscFinanceOptionSwitchToCash",
            "uscFinanceOptionSwitchToMonthly",
        ]);

    // Only used in case the finance prices are hidden
    const [cashToggleLabel, monthlyToggleLabel] = useUscLabel([
        "uscFinanceOptionCashToggle",
        "uscFinanceOptionMonthlyToggle",
    ]);

    const onToggle = (selected: FinanceOptionType): void => {
        trackFinanceToggle(trackingLocation, selected, vehicleType);
        dispatch(setFinanceOption(selected));
    };

    return (
        <Styles.Wrapper>
            <SrOnly as="legend">{activeFinanceOption === "cash" ? switchToMonthlyLabel : switchToCashLabel}</SrOnly>
            <Styles.ToggleWrapper>
                {enableMonthly ? (
                    <>
                        <Styles.ToggleItem
                            htmlFor={`finance-option-cash-${id}`}
                            data-is-active={activeFinanceOption === "cash" || undefined}
                        >
                            <Text variant="body-small-regular">
                                {activeFinanceOption === "cash" ? selectedCashLabel : switchToCashLabel}
                            </Text>
                            <Text variant="button-semibold">
                                {showFinancePrices ? <UscCashPrice hideDisclaimerIcons /> : cashToggleLabel}
                            </Text>
                            {cashLoading && (
                                <Styles.LoadingIconWrapper>
                                    <Preloader size={16} />
                                </Styles.LoadingIconWrapper>
                            )}
                            <input
                                name={`finance-toggle-${id}`}
                                id={`finance-option-cash-${id}`}
                                type="radio"
                                value="cash"
                                checked={activeFinanceOption === "cash"}
                                onChange={(event) => onToggle(event.target.value as FinanceOptionType)}
                            />
                        </Styles.ToggleItem>
                        <Styles.ToggleItem
                            htmlFor={`finance-option-monthly-${id}`}
                            data-is-active={activeFinanceOption === "monthly" || undefined}
                        >
                            <Text variant="body-small-regular">
                                {activeFinanceOption === "monthly" ? selectedMonthlyLabel : switchToMonthlyLabel}
                            </Text>
                            <Text variant="button-semibold">
                                {showFinancePrices ? (
                                    <span dangerouslySetInnerHTML={{ __html: monthly }} />
                                ) : (
                                    monthlyToggleLabel
                                )}
                            </Text>
                            {monthlyLoading && (
                                <Styles.LoadingIconWrapper>
                                    <Preloader size={16} />
                                </Styles.LoadingIconWrapper>
                            )}
                            <input
                                name={`finance-toggle-${id}`}
                                id={`finance-option-monthly-${id}`}
                                type="radio"
                                value="monthly"
                                checked={activeFinanceOption === "monthly"}
                                onChange={(event) => onToggle(event.target.value as FinanceOptionType)}
                            />
                        </Styles.ToggleItem>
                    </>
                ) : (
                    <Styles.ToggleItem htmlFor="financeOption" data-testid="cash-only-toggle">
                        <Text variant="body-small-regular">{cashOnlyLabel}</Text>
                        <Text variant="button-semibold">
                            {showFinancePrices ? <UscCashPrice hideDisclaimerIcons /> : cashToggleLabel}
                        </Text>
                        {cashLoading && (
                            <Styles.LoadingIconWrapper>
                                <Preloader size={16} />
                            </Styles.LoadingIconWrapper>
                        )}
                        <input
                            name={`finance-toggle-${id}`}
                            id={`finance-option-cash-${id}`}
                            type="radio"
                            value="cash"
                            checked
                            disabled
                        />
                    </Styles.ToggleItem>
                )}
            </Styles.ToggleWrapper>

            {showFinancePrices && !hasDualCurrency(commonSettings) ? (
                <>
                    {enableMonthly &&
                        activeFinanceOption === "monthly" &&
                        // In retailer screens, there's the ability to overwrite components used in USC
                        // to different components (or just to null). See componentRegistry.tsx
                        // The problem is that the needed component registry provider currently does not
                        // support being wrapped around a modal. Therefore, if opened in a modal,
                        // use the non-component registry version of the filter wrapper.
                        (inModal ? (
                            <ModalUscPersonaliseFinanceButtonWrapper onClick={showCalculator} />
                        ) : (
                            <PdpUscPersonaliseFinanceButton onClick={showCalculator} />
                        ))}
                    {showMonthlyDisclaimers && <UscMonthlyPriceDisclaimer isPlaceholder={monthlyLoading} />}
                </>
            ) : (
                <>
                    {inBasket && <UscInlinePrices inBasketDetails={inBasket} />}
                    {enableMonthly &&
                        activeFinanceOption === "monthly" &&
                        (inModal ? (
                            <ModalUscPersonaliseFinanceButtonWrapper onClick={showCalculator} />
                        ) : (
                            <PdpUscPersonaliseFinanceButton onClick={showCalculator} />
                        ))}
                    <InlineDisclaimer
                        disclaimerType={
                            activeFinanceOption === "cash" ? UscDisclaimerType.Cash : UscDisclaimerType.Finance
                        }
                    />
                </>
            )}
        </Styles.Wrapper>
    );
};

export default UscFinanceToggle;
